import { useState, useEffect, useMemo } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { isAxiosError } from 'axios';
import {
  GlobeAltIcon,
  LockClosedIcon,
  UserGroupIcon,
  CalendarIcon,
  TagIcon,
  PlayCircleIcon,
  ArrowLeftIcon,
  PencilSquareIcon,
  PlusIcon,
  ClipboardDocumentIcon,
  ShieldCheckIcon,
} from '@heroicons/react/24/outline';
import { CheckCircleIcon } from '@heroicons/react/24/solid';
import { useAuth } from '@/contexts/AuthContext';
import { getChannel, getChannelPublic } from '@/services/channelApi';
import type { Channel, ChannelPublicResponse } from '@/types/channel';
import Spinner from '@/components/ui/Spinner';
import { RequestToJoinModal } from '@/components/ui/RequestToJoinModal';
import {
  useCreateAccessRequest,
  useWithdrawAccessRequest,
} from '@/hooks/useChannelAccessRequests';
import { useTemplates } from '@/hooks/useTemplates';
import { TemplateCard } from '@/components/template';
import { formatDate } from '@/utils/formatters';
import { ChannelPostsTab } from '@/components/channel/ChannelPostsTab';
import { ChannelLeaderboardTab } from '@/components/channel/ChannelLeaderboardTab';
// B-2026-0078: first-timer tooltip
import { useOnboardingStatus } from '@/hooks/useOnboarding';
// S28: self-readable credit balance widget
import { CreditBalanceWidget } from '@/components/credits/CreditBalanceWidget';

/** localStorage key for the first-timer template tooltip dismiss counter. */
const FIRST_TIMER_TOOLTIP_KEY = 'sf_channel_template_tooltip_views';

/** Derive initials for the channel avatar fallback — PRESERVED from original */
function getInitials(name: string): string {
  return name
    .split(' ')
    .map((w) => w[0])
    .slice(0, 2)
    .join('')
    .toUpperCase();
}

/** Canonical public URL for sharing */
const CANONICAL_BASE = 'https://www.pointetracer.com';

function buildChannelUrl(slug: string): string {
  return `${CANONICAL_BASE}/channels/${slug}`;
}

/** Social icon SVGs — inline to avoid icon library dependency */
function InstagramIcon({ size = 16 }: { size?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
      <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z" />
    </svg>
  );
}

function YoutubeIcon({ size = 16 }: { size?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
      <path d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z" />
    </svg>
  );
}

function TiktokIcon({ size = 16 }: { size?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
      <path d="M12.525.02c1.31-.02 2.61-.01 3.91-.02.08 1.53.63 3.09 1.75 4.17 1.12 1.11 2.7 1.62 4.24 1.79v4.03c-1.44-.05-2.89-.35-4.2-.97-.57-.26-1.1-.59-1.62-.93-.01 2.92.01 5.84-.02 8.75-.08 1.4-.54 2.79-1.35 3.94-1.31 1.92-3.58 3.17-5.91 3.21-1.43.08-2.86-.31-4.08-1.03-2.02-1.19-3.44-3.37-3.65-5.71-.02-.5-.03-1-.01-1.49.18-1.9 1.12-3.72 2.58-4.96 1.66-1.44 3.98-2.13 6.15-1.72.02 1.48-.04 2.96-.04 4.44-.99-.32-2.15-.23-3.02.37-.63.41-1.11 1.04-1.36 1.75-.21.51-.15 1.07-.14 1.61.24 1.64 1.82 3.02 3.5 2.87 1.12-.01 2.19-.66 2.77-1.61.19-.33.4-.67.41-1.06.1-1.79.06-3.57.07-5.36.01-4.03-.01-8.05.02-12.07z" />
    </svg>
  );
}

function WebsiteIcon({ size = 16 }: { size?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.5} strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <circle cx="12" cy="12" r="10" />
      <ellipse cx="12" cy="12" rx="4" ry="10" />
      <line x1="2" y1="12" x2="22" y2="12" />
    </svg>
  );
}

/** Social link renderer */
function SocialLinks({ links }: { links: Record<string, string> }) {
  const items: Array<{ key: string; label: string; href: string; icon: React.ReactNode }> = [];

  if (links.instagram) {
    const handle = links.instagram.startsWith('http') ? links.instagram : `https://instagram.com/${links.instagram.replace(/^@/, '')}`;
    items.push({ key: 'instagram', label: 'Instagram', href: handle, icon: <InstagramIcon size={14} /> });
  }
  if (links.youtube) {
    const href = links.youtube.startsWith('http') ? links.youtube : `https://youtube.com/@${links.youtube}`;
    items.push({ key: 'youtube', label: 'YouTube', href, icon: <YoutubeIcon size={14} /> });
  }
  if (links.tiktok) {
    const handle = links.tiktok.startsWith('http') ? links.tiktok : `https://tiktok.com/@${links.tiktok.replace(/^@/, '')}`;
    items.push({ key: 'tiktok', label: 'TikTok', href: handle, icon: <TiktokIcon size={14} /> });
  }

  if (items.length === 0) return null;

  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginTop: '10px', flexWrap: 'wrap' }}>
      {items.map(({ key, label, href, icon }) => (
        <a
          key={key}
          href={href}
          target="_blank"
          rel="noopener noreferrer"
          aria-label={label}
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: '5px',
            padding: '5px 10px',
            borderRadius: '999px',
            backgroundColor: 'var(--color-dark-elevated-2)',
            border: '1px solid var(--color-border-subtle)',
            color: 'var(--color-text-secondary)',
            fontSize: '12px',
            fontWeight: 500,
            textDecoration: 'none',
            transition: 'border-color 150ms ease, color 150ms ease',
          }}
          onMouseEnter={(e) => {
            (e.currentTarget as HTMLAnchorElement).style.borderColor = 'var(--color-accent)';
            (e.currentTarget as HTMLAnchorElement).style.color = 'var(--color-accent)';
          }}
          onMouseLeave={(e) => {
            (e.currentTarget as HTMLAnchorElement).style.borderColor = 'var(--color-border-subtle)';
            (e.currentTarget as HTMLAnchorElement).style.color = 'var(--color-text-secondary)';
          }}
        >
          {icon}
          {label}
        </a>
      ))}
    </div>
  );
}

/** Copy channel link button */
function CopyLinkButton({ slug }: { slug: string }) {
  const [copied, setCopied] = useState(false);

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(buildChannelUrl(slug));
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // Fallback for browsers without clipboard API
      const ta = document.createElement('textarea');
      ta.value = buildChannelUrl(slug);
      document.body.appendChild(ta);
      ta.select();
      document.execCommand('copy');
      document.body.removeChild(ta);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  return (
    <button
      onClick={handleCopy}
      title="Copy channel link"
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: '6px',
        padding: '8px 14px',
        borderRadius: '999px',
        backgroundColor: copied ? 'rgba(30,215,96,0.12)' : 'var(--color-dark-elevated-2)',
        border: copied ? '1px solid rgba(30,215,96,0.3)' : '1px solid var(--color-border-strong)',
        color: copied ? 'var(--color-accent)' : 'var(--color-text-secondary)',
        fontSize: '13px',
        fontWeight: 500,
        cursor: 'pointer',
        transition: 'all 150ms ease',
        flexShrink: 0,
      }}
    >
      {copied ? (
        <>
          <CheckCircleIcon style={{ width: '14px', height: '14px' }} />
          Copied!
        </>
      ) : (
        <>
          <ClipboardDocumentIcon style={{ width: '14px', height: '14px' }} />
          Copy link
        </>
      )}
    </button>
  );
}

// ---------------------------------------------------------------------------
// Public landing page (unauthenticated visitor view)
// ---------------------------------------------------------------------------

interface PublicLandingProps {
  slug: string;
  pub: ChannelPublicResponse;
}

function PublicLanding({ slug, pub }: PublicLandingProps) {
  const navigate = useNavigate();
  const bannerStyle: React.CSSProperties = pub.cover_image_url
    ? { backgroundImage: `url(${pub.cover_image_url})`, backgroundSize: 'cover', backgroundPosition: 'center' }
    : { background: 'linear-gradient(135deg, var(--color-dark-elevated-1) 0%, var(--color-dark-base) 100%)' };

  return (
    <>
      <style>{`
        @keyframes pl-fade-in { from { opacity: 0; transform: translateY(12px); } to { opacity: 1; transform: translateY(0); } }
        .pl-page { animation: pl-fade-in 0.4s cubic-bezier(0.22, 1, 0.36, 1) both; }
        .pl-cta-btn { transition: background-color 150ms ease, box-shadow 150ms ease, transform 100ms ease; }
        .pl-cta-btn:hover { box-shadow: 0 0 20px rgba(30,215,96,0.35), 0 4px 12px rgba(0,0,0,0.25); transform: translateY(-1px); }
        .pl-cta-btn:active { transform: scale(0.97); }
      `}</style>

      <div className="pl-page" style={{ maxWidth: '820px', margin: '0 auto' }}>
        {/* Banner */}
        <div style={{ borderRadius: '20px', overflow: 'hidden', marginBottom: '32px', border: '1px solid var(--color-border-subtle)', boxShadow: 'var(--shadow-card)' }}>
          <div style={{ height: '200px', position: 'relative', ...bannerStyle }}>
            {/* overlay gradient for readability */}
            <div aria-hidden="true" style={{ position: 'absolute', inset: 0, background: 'linear-gradient(to bottom, transparent 40%, rgba(0,0,0,0.55) 100%)' }} />
            {!pub.cover_image_url && (
              <div aria-hidden="true" style={{ position: 'absolute', inset: 0, backgroundImage: 'radial-gradient(circle at 20% 50%, rgba(30,215,96,0.06) 0%, transparent 60%), radial-gradient(circle at 80% 30%, rgba(61,169,252,0.05) 0%, transparent 55%)' }} />
            )}
          </div>

          <div style={{ backgroundColor: 'var(--color-dark-card)', padding: '0 32px 32px' }}>
            {/* Logo avatar overlapping banner */}
            <div style={{ position: 'relative', marginTop: '-44px', marginBottom: '16px', display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between' }}>
              <div style={{ width: '88px', height: '88px', borderRadius: '20px', backgroundColor: 'var(--color-dark-elevated-2)', border: '3px solid var(--color-dark-card)', display: 'flex', alignItems: 'center', justifyContent: 'center', boxShadow: '0 4px 16px rgba(0,0,0,0.35)', overflow: 'hidden', flexShrink: 0 }}>
                {pub.school_logo_url ? (
                  <img src={pub.school_logo_url} alt={pub.display_name} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                ) : (
                  <span style={{ fontFamily: 'var(--font-heading)', fontWeight: 700, fontSize: '28px', color: 'var(--color-accent)', letterSpacing: '-1px' }}>{getInitials(pub.display_name)}</span>
                )}
              </div>
              <CopyLinkButton slug={slug} />
            </div>

            {/* Name */}
            <h1 style={{ fontFamily: 'var(--font-heading)', fontWeight: 700, fontSize: '26px', color: 'var(--color-text-primary)', margin: '0 0 6px', letterSpacing: '-0.4px', lineHeight: 1.2 }}>
              {pub.display_name}
            </h1>

            {/* Tagline */}
            {pub.tagline && (
              <p style={{ fontSize: '15px', color: 'var(--color-text-secondary)', margin: '0 0 12px', lineHeight: 1.5 }}>
                {pub.tagline}
              </p>
            )}

            {/* Badge row */}
            <div style={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: '8px', marginBottom: '12px' }}>
              {pub.sport_focus && (
                <span style={{ display: 'inline-flex', alignItems: 'center', gap: '5px', padding: '4px 10px', borderRadius: '999px', backgroundColor: 'rgba(61,169,252,0.12)', border: '1px solid rgba(61,169,252,0.25)', color: 'var(--color-accent-blue)', fontSize: '12px', fontWeight: 600 }}>
                  <TagIcon style={{ width: '12px', height: '12px' }} />
                  {pub.sport_focus}
                </span>
              )}
              {pub.accreditation && (
                <span style={{ display: 'inline-flex', alignItems: 'center', gap: '5px', padding: '4px 10px', borderRadius: '999px', backgroundColor: 'rgba(251,191,36,0.10)', border: '1px solid rgba(251,191,36,0.25)', color: '#F5C542', fontSize: '12px', fontWeight: 600 }}>
                  <ShieldCheckIcon style={{ width: '12px', height: '12px' }} />
                  {pub.accreditation}
                </span>
              )}
              {/* Always listed when this page is served, but show the badge defensively */}
              <span style={{ display: 'inline-flex', alignItems: 'center', gap: '5px', padding: '4px 10px', borderRadius: '999px', backgroundColor: 'rgba(30,215,96,0.10)', border: '1px solid rgba(30,215,96,0.22)', color: 'var(--color-accent)', fontSize: '12px', fontWeight: 600 }}>
                <GlobeAltIcon style={{ width: '12px', height: '12px' }} />
                Listed
              </span>
            </div>

            {/* Description */}
            {pub.description && (
              <p style={{ fontSize: '14px', color: 'var(--color-text-secondary)', lineHeight: 1.65, maxWidth: '580px', margin: '0 0 12px' }}>
                {pub.description}
              </p>
            )}

            {/* Social links */}
            {pub.social_links && Object.keys(pub.social_links).length > 0 && (
              <SocialLinks links={pub.social_links} />
            )}

            {/* Website */}
            {pub.website_url && (
              <a href={pub.website_url} target="_blank" rel="noopener noreferrer" style={{ display: 'inline-flex', alignItems: 'center', gap: '5px', marginTop: '10px', fontSize: '13px', color: 'var(--color-text-muted)', textDecoration: 'none' }}
                onMouseEnter={(e) => ((e.currentTarget as HTMLAnchorElement).style.color = 'var(--color-text-primary)')}
                onMouseLeave={(e) => ((e.currentTarget as HTMLAnchorElement).style.color = 'var(--color-text-muted)')}
              >
                <WebsiteIcon size={13} />
                {pub.website_url.replace(/^https?:\/\//, '')}
              </a>
            )}
          </div>
        </div>

        {/* Stats row — template_count is always 0 (content is membership-gated), so omit it */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '16px', marginBottom: '32px' }}>
          {[
            { label: 'Members', value: pub.member_count.toLocaleString() },
            { label: 'Recent Evals', value: pub.latest_eval_count.toLocaleString() },
          ].map(({ label, value }) => (
            <div key={label} style={{ backgroundColor: 'var(--color-dark-card)', border: '1px solid var(--color-border-subtle)', borderRadius: '14px', padding: '20px', textAlign: 'center', boxShadow: 'var(--shadow-card)' }}>
              <div style={{ fontFamily: 'var(--font-heading)', fontWeight: 700, fontSize: '28px', color: 'var(--color-text-primary)', marginBottom: '4px' }}>{value}</div>
              <div style={{ fontSize: '12px', color: 'var(--color-text-muted)', fontWeight: 500 }}>{label}</div>
            </div>
          ))}
        </div>

        {/* CTA card */}
        <div style={{ backgroundColor: 'var(--color-dark-card)', border: '1px solid var(--color-border-subtle)', borderRadius: '20px', padding: '36px 40px', textAlign: 'center', boxShadow: 'var(--shadow-card)' }}>
          <div style={{ width: '56px', height: '56px', borderRadius: '14px', backgroundColor: 'rgba(30,215,96,0.10)', border: '1px solid rgba(30,215,96,0.2)', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 16px' }}>
            <UserGroupIcon style={{ width: '26px', height: '26px', color: 'var(--color-accent)' }} />
          </div>
          <h2 style={{ fontFamily: 'var(--font-heading)', fontWeight: 700, fontSize: '20px', color: 'var(--color-text-primary)', margin: '0 0 8px' }}>
            Request to join
          </h2>
          <p style={{ fontSize: '14px', color: 'var(--color-text-muted)', margin: '0 0 24px', maxWidth: '380px', marginInline: 'auto', lineHeight: 1.6 }}>
            Sign in or create a free account to request access. The coach reviews all membership requests.
          </p>
          <div style={{ display: 'flex', gap: '12px', justifyContent: 'center', flexWrap: 'wrap' }}>
            <button
              onClick={() => navigate(`/login?redirect=/channels/${slug}`)}
              className="pl-cta-btn"
              style={{ padding: '12px 28px', borderRadius: '999px', backgroundColor: 'var(--color-accent)', border: 'none', color: 'var(--color-text-inverse)', fontSize: '14px', fontFamily: 'var(--font-heading)', fontWeight: 600, cursor: 'pointer' }}
            >
              Sign in
            </button>
            <button
              onClick={() => navigate(`/register?redirect=/channels/${slug}`)}
              style={{ padding: '12px 28px', borderRadius: '999px', backgroundColor: 'var(--color-dark-elevated-2)', border: '1px solid var(--color-border-strong)', color: 'var(--color-text-primary)', fontSize: '14px', fontFamily: 'var(--font-heading)', fontWeight: 600, cursor: 'pointer', transition: 'background-color 150ms ease' }}
              onMouseEnter={(e) => ((e.currentTarget as HTMLButtonElement).style.backgroundColor = 'var(--color-dark-card)')}
              onMouseLeave={(e) => ((e.currentTarget as HTMLButtonElement).style.backgroundColor = 'var(--color-dark-elevated-2)')}
            >
              Create account
            </button>
          </div>
        </div>
      </div>
    </>
  );
}

// ---------------------------------------------------------------------------
// Main ChannelPage component
// ---------------------------------------------------------------------------

// Valid tab values for the ChannelPage tab bar.
type ChannelTab = 'templates' | 'posts' | 'leaderboard';

export default function ChannelPage() {
  const { slug } = useParams<{ slug: string }>();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { user } = useAuth();

  // Derive active tab from ?tab= query param; default to 'templates'.
  const rawTab = searchParams.get('tab');
  const activeTab: ChannelTab =
    rawTab === 'posts' ? 'posts' :
    rawTab === 'leaderboard' ? 'leaderboard' :
    'templates';

  const setActiveTab = (tab: ChannelTab) => {
    setSearchParams(tab === 'templates' ? {} : { tab });
  };

  const [channel, setChannel] = useState<Channel | null>(null);
  const [publicData, setPublicData] = useState<ChannelPublicResponse | null>(null);
  const [isPublicFallback, setIsPublicFallback] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Access request state
  const [showRequestModal, setShowRequestModal] = useState(false);
  const [requestPending, setRequestPending] = useState(false);

  // B-2026-0078: first-timer tooltip on the template grid.
  // Show a pulsing "Start here" indicator on the first template card when the
  // athlete has not yet submitted an eval. Disappears after 2 page loads or
  // after the user clicks the first template (tracked via localStorage counter).
  const { data: onboardingStatus } = useOnboardingStatus();
  const [showFirstTimerTooltip, setShowFirstTimerTooltip] = useState<boolean>(() => {
    try {
      const views = parseInt(localStorage.getItem(FIRST_TIMER_TOOLTIP_KEY) ?? '0', 10);
      return !isNaN(views) && views < 2;
    } catch {
      return true;
    }
  });

  // Increment view counter on mount (only when we intend to show the tooltip).
  useEffect(() => {
    if (!showFirstTimerTooltip) return;
    try {
      const views = parseInt(localStorage.getItem(FIRST_TIMER_TOOLTIP_KEY) ?? '0', 10);
      localStorage.setItem(FIRST_TIMER_TOOLTIP_KEY, String((isNaN(views) ? 0 : views) + 1));
    } catch {
      // localStorage unavailable — tooltip will show without persistence
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Derived: show first-timer tooltip only for athletes who haven't submitted yet.
  const shouldShowFirstTimerTooltip =
    showFirstTimerTooltip &&
    onboardingStatus != null &&
    !onboardingStatus.has_submitted_eval;

  const createRequest = useCreateAccessRequest(slug ?? '');
  const withdrawRequest = useWithdrawAccessRequest(slug ?? '');

  const isOwner = user?.id === channel?.owner_id;

  // Fetch all templates and filter client-side to those belonging to this channel.
  // TODO: Replace client-side filter with `GET /api/templates?channel_id=<uuid>` server filter
  // when template count per coach > ~50. Acceptable for MVP scale.
  const { data: allTemplates, isLoading: templatesLoading } = useTemplates();
  const channelTemplates = useMemo(
    () => allTemplates?.filter((t) => t.channel_slug === slug) ?? [],
    [allTemplates, slug],
  );

  useEffect(() => {
    if (!slug) return;
    setIsLoading(true);
    setError(null);
    setIsPublicFallback(false);
    setPublicData(null);

    getChannel(slug)
      .then((ch) => {
        setChannel(ch);
      })
      .catch((err) => {
        // Task 7: If auth-gated (401), fall back to public endpoint
        if (isAxiosError(err) && err.response?.status === 401) {
          getChannelPublic(slug)
            .then((pub) => {
              setPublicData(pub);
              setIsPublicFallback(true);
            })
            .catch(() => setError('Channel not found or you do not have access.'));
        } else {
          setError('Channel not found or you do not have access.');
        }
      })
      .finally(() => setIsLoading(false));
  }, [slug]);

  if (isLoading) {
    return (
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '60vh' }}>
        <Spinner size="lg" label="Loading channel..." />
      </div>
    );
  }

  // Public fallback for unauthenticated visitors
  if (isPublicFallback && publicData && slug) {
    return <PublicLanding slug={slug} pub={publicData} />;
  }

  if (error || !channel) {
    return (
      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', minHeight: '60vh', gap: '16px' }}>
        <div style={{ width: '64px', height: '64px', borderRadius: '50%', backgroundColor: 'var(--color-dark-card)', border: '1px solid var(--color-border-subtle)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <LockClosedIcon style={{ width: '28px', height: '28px', color: 'var(--color-text-muted)' }} />
        </div>
        <p style={{ color: 'var(--color-text-secondary)', fontSize: '15px' }}>
          {error || 'Channel not found.'}
        </p>
        <button
          onClick={() => navigate(-1)}
          style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', color: 'var(--color-accent)', background: 'none', border: 'none', cursor: 'pointer', fontSize: '14px', fontWeight: 600 }}
        >
          <ArrowLeftIcon style={{ width: '16px', height: '16px' }} />
          Go back
        </button>
      </div>
    );
  }

  // Determine banner style — use uploaded banner if available, else gradient fallback (PRESERVED)
  const bannerBg: React.CSSProperties = channel.cover_image_url
    ? { backgroundImage: `url(${channel.cover_image_url})`, backgroundSize: 'cover', backgroundPosition: 'center' }
    : { background: 'linear-gradient(135deg, var(--color-dark-elevated-1) 0%, var(--color-dark-base) 100%)' };

  return (
    <>
      <style>{`
        @keyframes ch-fade-in {
          from { opacity: 0; transform: translateY(12px); }
          to   { opacity: 1; transform: translateY(0); }
        }
        .ch-page { animation: ch-fade-in 0.4s cubic-bezier(0.22, 1, 0.36, 1) both; }

        .ch-template-card {
          background-color: var(--color-dark-card);
          border: 1px solid var(--color-border-subtle);
          border-radius: 16px;
          padding: 20px;
          transition: border-color 200ms ease, transform 200ms ease, box-shadow 200ms ease;
          cursor: var(--cursor-hand);
        }
        .ch-template-card:hover {
          border-color: var(--color-accent);
          transform: translateY(-2px) scale(1.01);
          box-shadow: 0 12px 32px rgba(0, 0, 0, 0.3), 0 0 0 1px rgba(30, 215, 96, 0.15);
        }

        .ch-back-btn {
          transition: color 150ms ease;
        }

        /* B-2026-0078 first-timer pulse ring on first template card */
        @keyframes sf-first-timer-pulse {
          0%   { box-shadow: 0 0 0 0 rgba(30, 215, 96, 0.5); }
          70%  { box-shadow: 0 0 0 8px rgba(30, 215, 96, 0); }
          100% { box-shadow: 0 0 0 0 rgba(30, 215, 96, 0); }
        }
        .sf-first-timer-ring {
          animation: sf-first-timer-pulse 2s ease-in-out infinite;
          border-color: rgba(30, 215, 96, 0.6) !important;
        }
        .ch-back-btn:hover {
          color: var(--color-text-primary) !important;
        }
      `}</style>

      <div className="ch-page" style={{ maxWidth: '1100px', margin: '0 auto' }}>

        {/* Back navigation */}
        <button
          onClick={() => navigate(-1)}
          className="ch-back-btn"
          style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', color: 'var(--color-text-muted)', background: 'none', border: 'none', cursor: 'pointer', fontSize: '14px', marginBottom: '24px', padding: 0 }}
        >
          <ArrowLeftIcon style={{ width: '16px', height: '16px' }} />
          Back
        </button>

        {/* ── Hero Section ────────────────────────────────────────────── */}
        <div style={{ borderRadius: '20px', border: '1px solid var(--color-border-subtle)', overflow: 'hidden', marginBottom: '32px', boxShadow: 'var(--shadow-card)' }}>

          {/* Banner — uploaded image or gradient fallback (PRESERVED) */}
          <div style={{ height: '200px', position: 'relative', overflow: 'hidden', ...bannerBg }}>
            {/* Subtle grid texture overlay — shown on both image and gradient banners */}
            <div
              aria-hidden="true"
              style={{ position: 'absolute', inset: 0, background: channel.cover_image_url ? 'linear-gradient(to bottom, transparent 40%, rgba(0,0,0,0.45) 100%)' : 'radial-gradient(circle at 20% 50%, rgba(30,215,96,0.06) 0%, transparent 60%), radial-gradient(circle at 80% 30%, rgba(61,169,252,0.05) 0%, transparent 55%)' }}
            />
          </div>

          {/* Profile row */}
          <div style={{ backgroundColor: 'var(--color-dark-card)', padding: '0 32px 28px' }}>
            {/* Logo avatar — overlaps the banner */}
            <div style={{ position: 'relative', marginTop: '-44px', marginBottom: '16px', display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between' }}>
              <div
                style={{ width: '88px', height: '88px', borderRadius: '20px', backgroundColor: 'var(--color-dark-elevated-2)', border: '3px solid var(--color-dark-card)', display: 'flex', alignItems: 'center', justifyContent: 'center', boxShadow: '0 4px 16px rgba(0,0,0,0.35)', overflow: 'hidden', flexShrink: 0 }}
              >
                {/* PRESERVED: gradient + initials fallback when school_logo_url is null */}
                {channel.school_logo_url ? (
                  <img src={channel.school_logo_url} alt={channel.display_name} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                ) : (
                  <span style={{ fontFamily: 'var(--font-heading)', fontWeight: 700, fontSize: '28px', color: 'var(--color-accent)', letterSpacing: '-1px' }}>
                    {getInitials(channel.display_name)}
                  </span>
                )}
              </div>

              {/* Copy link button — top-right of avatar row */}
              <CopyLinkButton slug={channel.slug} />
            </div>

            {/* Name + meta row */}
            <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', flexWrap: 'wrap', gap: '16px' }}>
              <div style={{ flex: 1, minWidth: 0 }}>
                <h1 style={{ fontFamily: 'var(--font-heading)', fontWeight: 700, fontSize: '26px', color: 'var(--color-text-primary)', margin: '0 0 4px', letterSpacing: '-0.4px', lineHeight: 1.2 }}>
                  {channel.display_name}
                </h1>

                {/* Tagline */}
                {channel.tagline && (
                  <p style={{ fontSize: '15px', color: 'var(--color-text-secondary)', margin: '0 0 12px', lineHeight: 1.5, maxWidth: '560px' }}>
                    {channel.tagline}
                  </p>
                )}

                {/* Badges row */}
                <div style={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: '8px' }}>
                  {/* Sport focus */}
                  {channel.sport_focus && (
                    <span style={{ display: 'inline-flex', alignItems: 'center', gap: '5px', padding: '4px 10px', borderRadius: '999px', backgroundColor: 'rgba(61,169,252,0.12)', border: '1px solid rgba(61,169,252,0.25)', color: 'var(--color-accent-blue)', fontSize: '12px', fontWeight: 600 }}>
                      <TagIcon style={{ width: '12px', height: '12px' }} />
                      {channel.sport_focus}
                    </span>
                  )}

                  {/* Accreditation badge */}
                  {channel.accreditation && (
                    <span style={{ display: 'inline-flex', alignItems: 'center', gap: '5px', padding: '4px 10px', borderRadius: '999px', backgroundColor: 'rgba(251,191,36,0.10)', border: '1px solid rgba(251,191,36,0.25)', color: '#F5C542', fontSize: '12px', fontWeight: 600 }}>
                      <ShieldCheckIcon style={{ width: '12px', height: '12px' }} />
                      {channel.accreditation}
                    </span>
                  )}

                  {/* Listed / discovery badge */}
                  <span style={{ display: 'inline-flex', alignItems: 'center', gap: '5px', padding: '4px 10px', borderRadius: '999px', backgroundColor: channel.listed ? 'rgba(30,215,96,0.10)' : 'rgba(135,147,163,0.12)', border: channel.listed ? '1px solid rgba(30,215,96,0.22)' : '1px solid rgba(135,147,163,0.22)', color: channel.listed ? 'var(--color-accent)' : 'var(--color-text-muted)', fontSize: '12px', fontWeight: 600 }}>
                    {channel.listed ? (<GlobeAltIcon style={{ width: '12px', height: '12px' }} />) : (<LockClosedIcon style={{ width: '12px', height: '12px' }} />)}
                    {channel.listed ? 'Listed' : 'Unlisted'}
                  </span>

                  {/* Member count */}
                  <span style={{ display: 'inline-flex', alignItems: 'center', gap: '5px', color: 'var(--color-text-muted)', fontSize: '13px' }}>
                    <UserGroupIcon style={{ width: '14px', height: '14px' }} />
                    {channel.member_count.toLocaleString()}{' '}
                    {channel.member_count === 1 ? 'member' : 'members'}
                  </span>

                  {/* Created date */}
                  <span style={{ display: 'inline-flex', alignItems: 'center', gap: '5px', color: 'var(--color-text-muted)', fontSize: '13px' }}>
                    <CalendarIcon style={{ width: '14px', height: '14px' }} />
                    Joined {formatDate(channel.created_at, 'MMMM d, yyyy')}
                  </span>
                </div>

                {/* Social link icons row */}
                {channel.social_links && Object.keys(channel.social_links).length > 0 && (
                  <SocialLinks links={channel.social_links} />
                )}

                {/* Website link */}
                {channel.website_url && (
                  <a
                    href={channel.website_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{ display: 'inline-flex', alignItems: 'center', gap: '5px', marginTop: '10px', fontSize: '13px', color: 'var(--color-text-muted)', textDecoration: 'none' }}
                    onMouseEnter={(e) => ((e.currentTarget as HTMLAnchorElement).style.color = 'var(--color-text-primary)')}
                    onMouseLeave={(e) => ((e.currentTarget as HTMLAnchorElement).style.color = 'var(--color-text-muted)')}
                  >
                    <WebsiteIcon size={13} />
                    {channel.website_url.replace(/^https?:\/\//, '')}
                  </a>
                )}

                {/* Description */}
                {channel.description && (
                  <p style={{ marginTop: '14px', fontSize: '14px', color: 'var(--color-text-secondary)', lineHeight: 1.65, maxWidth: '600px' }}>
                    {channel.description}
                  </p>
                )}
              </div>

              {/* Action buttons */}
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flexShrink: 0 }}>
                {isOwner ? (
                  <button
                    onClick={() => navigate(`/channels/${slug}/manage`)}
                    style={{ display: 'inline-flex', alignItems: 'center', gap: '7px', padding: '10px 20px', borderRadius: '999px', backgroundColor: 'var(--color-dark-elevated-2)', border: '1px solid var(--color-border-strong)', color: 'var(--color-text-primary)', fontSize: '14px', fontWeight: 600, cursor: 'pointer', transition: 'background-color 150ms ease' }}
                    onMouseEnter={(e) => ((e.currentTarget as HTMLButtonElement).style.backgroundColor = 'var(--color-dark-card)')}
                    onMouseLeave={(e) => ((e.currentTarget as HTMLButtonElement).style.backgroundColor = 'var(--color-dark-elevated-2)')}
                  >
                    <PencilSquareIcon style={{ width: '15px', height: '15px' }} />
                    Manage Channel
                  </button>
                ) : (
                  /* Non-owner: show Request to Join or pending state.
                   * Self-join is removed (POST /channels/{slug}/join → 410 Gone).
                   * Membership is always coach-gated via invite or request→approve. */
                  <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: '6px' }}>
                    {requestPending ? (
                      <>
                        <span
                          data-testid="request-pending-badge"
                          style={{ display: 'inline-flex', alignItems: 'center', gap: '7px', padding: '10px 20px', borderRadius: '999px', backgroundColor: 'rgba(135,147,163,0.12)', border: '1px solid rgba(135,147,163,0.22)', color: 'var(--color-text-muted)', fontSize: '14px', fontWeight: 600 }}
                        >
                          Request pending
                        </span>
                        <button
                          onClick={() => { withdrawRequest.mutate(undefined, { onSuccess: () => setRequestPending(false) }); }}
                          disabled={withdrawRequest.isPending}
                          style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: '12px', color: 'var(--color-accent-red)', padding: 0, textDecoration: 'underline' }}
                        >
                          Withdraw request
                        </button>
                      </>
                    ) : (
                      <button
                        data-testid="request-access-btn"
                        onClick={() => setShowRequestModal(true)}
                        style={{ display: 'inline-flex', alignItems: 'center', gap: '7px', padding: '10px 24px', borderRadius: '999px', backgroundColor: 'rgba(61,169,252,0.12)', border: '1px solid rgba(61,169,252,0.25)', color: 'var(--color-accent-blue)', fontSize: '14px', fontFamily: 'var(--font-heading)', fontWeight: 600, cursor: 'pointer', transition: 'background-color 150ms ease' }}
                        onMouseEnter={(e) => ((e.currentTarget as HTMLButtonElement).style.backgroundColor = 'rgba(61,169,252,0.20)')}
                        onMouseLeave={(e) => ((e.currentTarget as HTMLButtonElement).style.backgroundColor = 'rgba(61,169,252,0.12)')}
                      >
                        Request to Join
                      </button>
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* ── Tab Bar ─────────────────────────────────────────────────── */}
        {/* B-2026-0074: Posts tab added alongside Templates. */}
        {/* B-2026-0072: Leaderboard tab added as third tab (hidden when leaderboard_enabled=false). */}
        {/* SHARED-FILE NOTE: Do NOT edit template-grid area below (owned by B-2026-0078). */}
        <div style={{ marginBottom: '24px' }}>
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '4px',
              borderBottom: '1px solid var(--color-border-subtle)',
              marginBottom: '0',
            }}
          >
            {([
              { id: 'templates' as ChannelTab, label: 'Templates', show: true },
              { id: 'posts' as ChannelTab, label: 'Posts', show: true },
              { id: 'leaderboard' as ChannelTab, label: 'Leaderboard', show: channel.leaderboard_enabled },
            ] as { id: ChannelTab; label: string; show: boolean }[])
              .filter((tab) => tab.show)
              .map(({ id, label }) => {
                const isActive = activeTab === id;
                return (
                  <button
                    key={id}
                    type="button"
                    onClick={() => setActiveTab(id)}
                    style={{
                      padding: '10px 18px',
                      fontSize: '14px',
                      fontWeight: isActive ? 600 : 400,
                      fontFamily: 'var(--font-heading)',
                      color: isActive ? 'var(--color-text-primary)' : 'var(--color-text-muted)',
                      background: 'none',
                      border: 'none',
                      borderBottom: isActive ? '2px solid var(--color-accent)' : '2px solid transparent',
                      marginBottom: '-1px',
                      cursor: 'pointer',
                      transition: 'color 120ms ease, border-color 120ms ease',
                    }}
                  >
                    {label}
                  </button>
                );
              })}
          </div>
        </div>

        {/* ── Templates Tab ────────────────────────────────────────────── */}
        {activeTab === 'templates' && (
          <div>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '20px' }}>
              <h2 style={{ fontFamily: 'var(--font-heading)', fontWeight: 600, fontSize: '18px', color: 'var(--color-text-primary)', margin: 0 }}>
                Templates
              </h2>
              {/* S28: self-readable credit balance for non-owner members (athletes/viewers).
                  Owners manage credits via the channel manage panel, not this widget.
                  Degrades to null when hook unavailable (flag off / error). */}
              {!isOwner && slug && (
                <CreditBalanceWidget slug={slug} />
              )}
              {/* Persistent Create Template CTA for channel owners — visible even when templates exist */}
              {isOwner && channel && (
                <button
                  onClick={() => navigate(`/templates/create?channel_id=${channel.id}&channel_slug=${channel.slug}`)}
                  style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', padding: '8px 16px', borderRadius: '999px', backgroundColor: 'var(--color-dark-elevated-2)', border: '1px solid var(--color-border-strong)', color: 'var(--color-text-primary)', fontSize: '13px', fontWeight: 600, cursor: 'pointer', transition: 'background-color 150ms ease' }}
                  onMouseEnter={(e) => ((e.currentTarget as HTMLButtonElement).style.backgroundColor = 'var(--color-dark-card)')}
                  onMouseLeave={(e) => ((e.currentTarget as HTMLButtonElement).style.backgroundColor = 'var(--color-dark-elevated-2)')}
                >
                  <PlusIcon style={{ width: '14px', height: '14px' }} />
                  New Template
                </button>
              )}
            </div>

            {/* Loading state */}
            {templatesLoading ? (
              <div style={{ display: 'flex', justifyContent: 'center', padding: '48px 0' }}>
                <Spinner size="lg" label="Loading templates..." />
              </div>
            ) : channelTemplates.length === 0 ? (
              /* Empty state */
              <div style={{ backgroundColor: 'var(--color-dark-card)', border: '1px dashed var(--color-border-strong)', borderRadius: '20px', padding: '64px 32px', textAlign: 'center' }}>
                <div style={{ width: '72px', height: '72px', borderRadius: '18px', backgroundColor: 'var(--color-dark-elevated-2)', border: '1px solid var(--color-border-subtle)', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 20px' }}>
                  <PlayCircleIcon style={{ width: '32px', height: '32px', color: 'var(--color-text-muted)' }} />
                </div>
                <h3 style={{ fontFamily: 'var(--font-heading)', fontWeight: 600, fontSize: '17px', color: 'var(--color-text-primary)', margin: '0 0 8px' }}>
                  No templates yet
                </h3>
                <p style={{ fontSize: '14px', color: 'var(--color-text-muted)', lineHeight: 1.6, maxWidth: '360px', margin: '0 auto' }}>
                  Templates added to this channel will appear here. Check back soon!
                </p>
                {isOwner && channel && (
                  <button
                    onClick={() => navigate(`/templates/create?channel_id=${channel.id}&channel_slug=${channel.slug}`)}
                    style={{ marginTop: '24px', display: 'inline-flex', alignItems: 'center', gap: '7px', padding: '10px 22px', borderRadius: '999px', backgroundColor: 'var(--color-accent)', border: 'none', color: 'var(--color-text-inverse)', fontSize: '14px', fontFamily: 'var(--font-heading)', fontWeight: 600, cursor: 'pointer', transition: 'background-color 150ms ease' }}
                    onMouseEnter={(e) => ((e.currentTarget as HTMLButtonElement).style.backgroundColor = 'var(--color-accent-hover)')}
                    onMouseLeave={(e) => ((e.currentTarget as HTMLButtonElement).style.backgroundColor = 'var(--color-accent)')}
                  >
                    <PlusIcon style={{ width: '14px', height: '14px' }} />
                    Open Coaching Studio
                  </button>
                )}
              </div>
            ) : (
              /* Templates grid — first card carries the B-2026-0078 first-timer tooltip */
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: '20px' }}>
                {channelTemplates.map((t, idx) => {
                  const isFirst = idx === 0;
                  const showHint = isFirst && shouldShowFirstTimerTooltip;
                  return (
                    // B-2026-0078: wrap first card in a relative container for the tooltip.
                    <div
                      key={t.id}
                      style={{ position: 'relative' }}
                      onClick={() => {
                        if (showHint) {
                          // Dismiss tooltip on first click
                          setShowFirstTimerTooltip(false);
                          try {
                            localStorage.setItem(FIRST_TIMER_TOOLTIP_KEY, '99');
                          } catch { /* noop */ }
                        }
                      }}
                    >
                      <TemplateCard
                        template={t}
                        onSelect={(template) => navigate(`/templates/${template.id}`)}
                        onEvaluate={(templateId) => navigate(`/evaluate/${templateId}`)}
                        canEditVisibility={user?.role === 'coach' || user?.role === 'admin'}
                        className={showHint ? 'sf-first-timer-ring' : undefined}
                      />
                      {/* First-timer callout badge */}
                      {showHint && (
                        <div
                          data-testid="first-timer-tooltip"
                          style={{
                            position: 'absolute',
                            top: '-10px',
                            left: '50%',
                            transform: 'translateX(-50%)',
                            backgroundColor: 'var(--color-accent)',
                            color: 'var(--color-text-inverse)',
                            fontSize: '11px',
                            fontWeight: 700,
                            padding: '3px 10px',
                            borderRadius: '999px',
                            whiteSpace: 'nowrap',
                            pointerEvents: 'none',
                            boxShadow: '0 2px 8px rgba(30,215,96,0.4)',
                          }}
                        >
                          Start here — tap to evaluate
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}

        {/* ── Posts Tab — B-2026-0074 ──────────────────────────────────── */}
        {activeTab === 'posts' && slug && (
          <ChannelPostsTab slug={slug} isOwner={isOwner} />
        )}

        {/* ── Leaderboard Tab — B-2026-0072 ───────────────────────────── */}
        {activeTab === 'leaderboard' && slug && channel.leaderboard_enabled && (
          <ChannelLeaderboardTab slug={slug} />
        )}

        {/* Request-to-Join modal */}
        <RequestToJoinModal
          isOpen={showRequestModal}
          onClose={() => setShowRequestModal(false)}
          channelName={channel.display_name}
          onSubmit={async (message, requestedRole) => {
            await createRequest.mutateAsync({ message: message || undefined, requested_role: requestedRole });
            setRequestPending(true);
          }}
        />
      </div>
    </>
  );
}
